import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPayrollRecords,
  generatePayroll,
  approvePayroll,
  markPayrollAsPaid
} from '../../redux/payrollSlice';
import { fetchEmployeeSalaries } from '../../redux/employeeSalarySlice';
import { 
  fetchLmsEmployees
} from '../../redux/erpSlice';
import { X, Plus, Eye, CheckCircle, XCircle, Calendar, Users, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';

const PayrollManagement = () => {
  const dispatch = useDispatch();
  const { payrollRecords, status, isGenerating } = useSelector((state) => state.payroll);
  const { employeeSalaries } = useSelector((state) => state.employeeSalaries);
  const { lmsEmployees, loading, totalCount, pageNumber, pageSize } = useSelector((state) => state.lmsEmployees);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isGeneratingPayroll, setIsGeneratingPayroll] = useState(false);
  const [showPayrollGenerationModal, setShowPayrollGenerationModal] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    dispatch(fetchPayrollRecords());
    dispatch(fetchEmployeeSalaries());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  const handleGeneratePayroll = async () => {
    if (!selectedMonth || !selectedYear) {
      toast.error('Please select month and year');
      return;
    }

    try {
      const result = await dispatch(generatePayroll({ month: selectedMonth, year: selectedYear })).unwrap();
      toast.success(`Payroll generated for ${result.length} employees`);
      dispatch(fetchPayrollRecords()); // Refresh the payroll list
    } catch (error) {
      toast.error(`Failed to generate payroll: ${error.message || 'Unknown error'}`);
    }
  };

  const handleApprovePayroll = async (id, approvalType) => {
    try {
      await dispatch(approvePayroll({ id, approvalType, status: 'Approved' })).unwrap();
      toast.success(`${approvalType.toUpperCase()} approval successful`);
      dispatch(fetchPayrollRecords()); // Refresh the payroll list
    } catch (error) {
      toast.error(`Failed to approve ${approvalType}: ${error.message || 'Unknown error'}`);
    }
  };

  const handleMarkAsPaid = async (id) => {
    try {
      await dispatch(markPayrollAsPaid({ id, paymentStatus: 'Paid' })).unwrap();
      toast.success('Salary marked as paid');
      dispatch(fetchPayrollRecords()); // Refresh the payroll list
    } catch (error) {
      toast.error(`Failed to mark salary as paid: ${error.message || 'Unknown error'}`);
    }
  };

  const filteredSalaries = payrollRecords.filter(salary => {
    const matchesTeacher = !filterTeacher || salary.teacherEmail.includes(filterTeacher);
    const matchesStatus = !filterStatus || salary.hrApproval === filterStatus;
    return matchesTeacher && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Partially Paid': return 'bg-orange-100 text-orange-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate summary statistics
  const summaryStats = {
    totalSalaries: filteredSalaries.length,
    totalAmount: filteredSalaries.reduce((sum, salary) => sum + (salary.netSalary || 0), 0),
    approvedCount: filteredSalaries.filter(s => s.hrApproval === 'Approved').length,
    paidCount: filteredSalaries.filter(s => s.paymentStatus === 'Paid').length,
    pendingCount: filteredSalaries.filter(s => s.hrApproval === 'Pending').length
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Payroll Management</h1>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <button
            onClick={() => setShowPayrollGenerationModal(true)}
            className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 text-sm sm:text-base"
            disabled={isGeneratingPayroll}
          >
            <Calendar size={18} />
            <span className="hidden sm:inline">{isGeneratingPayroll ? 'Generating...' : 'Generate Payroll'}</span>
            <span className="sm:hidden">{isGeneratingPayroll ? 'Generating...' : 'Generate'}</span>
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-[#241763] text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-[#1a0f47] text-sm sm:text-base"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add Salary Record</span>
            <span className="sm:hidden">Add Record</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="flex items-center justify-between p-4 rounded shadow border">
          <div className="flex flex-col space-y-2">
            <span className="text-2xl font-bold text-gray-800">{summaryStats.totalSalaries}</span>
            <span className="text-sm text-gray-600 font-medium">Total Salaries</span>
          </div>
          <div className="text-xl p-2 rounded-full bg-blue-100">
            <Users className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 rounded shadow border">
          <div className="flex flex-col space-y-2">
            <span className="text-2xl font-bold text-gray-800">{summaryStats.approvedCount}</span>
            <span className="text-sm text-gray-600 font-medium">Approved</span>
          </div>
          <div className="text-xl p-2 rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 rounded shadow border">
          <div className="flex flex-col space-y-2">
            <span className="text-2xl font-bold text-gray-800">{summaryStats.pendingCount}</span>
            <span className="text-sm text-gray-600 font-medium">Pending</span>
          </div>
          <div className="text-xl p-2 rounded-full bg-yellow-100">
            <XCircle className="h-6 w-6 text-yellow-600" />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 rounded shadow border">
          <div className="flex flex-col space-y-2">
            <span className="text-2xl font-bold text-gray-800">${summaryStats.totalAmount.toLocaleString()}</span>
            <span className="text-sm text-gray-600 font-medium">Total Amount</span>
          </div>
          <div className="text-xl p-2 rounded-full bg-green-100">
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        >
          <option value="">All Months</option>
          {months.map(month => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search by email..."
          value={filterTeacher}
          onChange={(e) => setFilterTeacher(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Payroll Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr>
              <th>Employee</th>
              <th>Month/Year</th>
              <th>Base Salary</th>
              <th>Allowances</th>
              <th>Deductions</th>
              <th>Net Salary</th>
              <th>HR Approval</th>
              <th>Finance Approval</th>
              <th>Payment Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {filteredSalaries.map((salary) => (
              <tr key={salary.id} className="p-2 py-2 hover:bg-gray-200 space-y-2">
                <td>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{salary.employeeName}</div>
                    <div className="text-xs text-gray-500">{salary.teacherEmail}</div>
                  </div>
                </td>
                <td>{salary.month} {salary.year}</td>
                <td>${salary.baseSalary?.toLocaleString()}</td>
                <td>${(salary.allowanceAmount + salary.bonusAmount + salary.overtimeAmount)?.toLocaleString()}</td>
                <td>${salary.deductionAmount?.toLocaleString()}</td>
                <td className="font-medium">${salary.netSalary?.toLocaleString()}</td>
                <td>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(salary.hrApproval)}`}>
                    {salary.hrApproval}
                  </span>
                </td>
                <td>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(salary.financeApproval)}`}>
                    {salary.financeApproval}
                  </span>
                </td>
                <td>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(salary.paymentStatus)}`}>
                    {salary.paymentStatus}
                  </span>
                </td>
                <td className="space-x-2 flex items-center py-2">
                  <button
                    onClick={() => handleApprovePayroll(salary.id, 'hr')}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="Approve HR"
                    disabled={salary.hrApproval === 'Approved'}
                  >
                    <CheckCircle className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleApprovePayroll(salary.id, 'finance')}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="Approve Finance"
                    disabled={salary.financeApproval === 'Approved'}
                  >
                    <CheckCircle className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleMarkAsPaid(salary.id)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="Mark as Paid"
                    disabled={salary.paymentStatus === 'Paid'}
                  >
                    <CheckCircle className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => {/* View details */}}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="View Details"
                  >
                    <Eye className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payroll Generation Modal */}
      {showPayrollGenerationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">Generate Payroll</h2>
              <button onClick={() => setShowPayrollGenerationModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Month</option>
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 p-3 sm:p-4 rounded-md">
                <p className="text-xs sm:text-sm text-blue-800">
                  This will generate payroll for all active employees based on their attendance, leave records, and salary configurations.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  onClick={() => setShowPayrollGenerationModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGeneratePayroll}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  disabled={!selectedMonth || !selectedYear || isGeneratingPayroll}
                >
                  {isGeneratingPayroll ? 'Generating...' : 'Generate Payroll'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for adding/editing salary records */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">Add Salary Record</h2>
              <button onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="text-center py-6 sm:py-8">
              <p className="text-sm sm:text-base text-gray-600">Salary records are typically generated automatically from employee salary configurations and attendance data.</p>
              <p className="text-sm sm:text-base text-gray-600 mt-2">Use the "Generate Payroll" button to create salary records for all employees.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement; 